import { useEffect } from 'react'

interface Props {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  /**
   * 7.9C-FIX-1 — when true, the dialog is in an in-flight operation:
   * Escape, backdrop click and the Cancel button are all locked out so the
   * user can't dismiss the modal mid-request (which would leave the UI in a
   * stuck "Excluindo…" state without ever closing). The confirm button is
   * also disabled to prevent double-submits.
   */
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * 7.8I — lightweight confirmation dialog used for destructive conversation
 * actions. Renders inline rather than via a portal because the workspace is
 * single-window and the overlay sits above every shell element.
 *
 * Closes on Escape, prevents the underlying chat from receiving focus while
 * open, and exposes a danger styling for delete-class confirmations.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
      if (e.key === 'Enter' && !busy) onConfirm()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, busy, onCancel, onConfirm])

  if (!open) return null

  const handleBackdropClick = () => {
    if (busy) return
    onCancel()
  }

  return (
    <div
      className="confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      aria-busy={busy || undefined}
      data-testid="confirm-dialog"
      onClick={handleBackdropClick}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="confirm-title">{title}</h3>
        <p>{message}</p>
        <div className="actions">
          <button
            type="button"
            className="btn"
            data-testid="confirm-cancel"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn btn-danger' : 'btn'}
            data-testid="confirm-accept"
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
